import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import "../styles/InvoiceList.css";
import NavBar from "./Navbar";
import TopNavBar from "./TopNavbar";
import LoaderForm from "./LoaderForm";
import { TailSpin } from "react-loader-spinner";
import Pagination from "./Pagination";
import { fetchInvoices, fetchInvoiceCount } from "../services/InvoiceService";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dropDownIcon from "../images/dropDownIcon.svg";
import { useAuth } from "../auth/auth";
import editIcon from "../images/editIcon.svg";
import deleteIcon from "../images/deleteIcon.svg";
import DefaultEmployeeProfile from "../images/DefaultEmployeeProfile.svg";
import {
  deniedActionClass,
  usePermissionGuard,
} from "../hooks/usePermissionGuard";
import { PERMISSIONS } from "../utils/rbac";

const apiUrl = process.env.REACT_APP_API_URL;

/** Payslip invoice list: only employees eligible for payslips (excludes invoice-only staff). Matches /getAllUsers server filter. */
function isEmployeeEligibleForPayslipList(employee) {
  if (employee == null) return false;
  const p = employee.isPayslipEligible;
  if (p === undefined || p === null) return true;
  return Number(p) === 1;
}

function convertDateFormat(dateString) {
  const mnths = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };

  const dateArray = dateString.split(" ");
  const year = dateArray[3];
  const month = mnths[dateArray[1]];
  const day = dateArray[2];

  return `${year}-${month}-${day}`;
}

function InvoiceList() {
  useAuth();
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();
  const canGeneratePdf = isAllowed(PERMISSIONS.GENERATE_PDF);
  const canEditPayslip = isAllowed(PERMISSIONS.EDIT);
  const canDeletePayslip = isAllowed(PERMISSIONS.DELETE);
  const canManageYtd = isAllowed(PERMISSIONS.MANAGE_YTD);
  const [invoices, setInvoices] = useState([]);
  const [deleteInvoiceId, setInvoiceToDelete] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [userEmail, setuserEmail] = useState();
  const [userName, setUserName] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isYtdModalOpen, setIsYtdModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [editedYtdAmount, setEditedYtdAmount] = useState("");
  const [currentInvoiceData, setCurrentInvoiceData] = useState(null);
  // const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [searchResult, setSearchResult] = useState([]);
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  const [count, setCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedType, setSelectedType] = useState(""); // Type can be 'user' or 'date' or 'both'
  const [isBothSelected, setIsBothSelected] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [dateChangeFlag, setDateChangeFlag] = useState(false);
  const [message, setMessage] = useState(null);
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);
  const selectRef = useRef(null);
  const employeeSearchInputRef = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);
  // const [showModal, setShowModal] = useState(false);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(pageParam);

  const toggleStartDateDropdown = () => {
    if (startDatePickerRef.current) {
      startDatePickerRef.current.setOpen(true);
    }
  };

  // const ConfirmDeleteModal = ({ id, onDeleteUser, onClose }) => {
  //   return (
  //     <div className="custom-modal">
  //       <div className="modal-content">
  //         <div className="left-container">
  //           <img src={dustbin} alt="Dustbin" className="dustbin-image" />
  //         </div>
  //         <div className="right-container">
  //           <p className="delete-warning-p">{`Are you sure you want to delete this profile?`}</p>
  //           <div className="button-container">
  //             <button
  //               className="delete-button"
  //               onClick={() => {
  //                 onDeleteUser(id);
  //                 onClose();
  //               }}
  //             >
  //               Delete
  //             </button>
  //             <button className="cancel-button" onClick={onClose}>
  //               Cancel
  //             </button>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  const handleEditYtdSubmit = async () => {
    try {
      // Use the new updateYTDFinal endpoint - user enters final YTD, system calculates adjustment
      setLoading(true);
      const response = await axios.post(`${apiUrl}/updateYTDFinal`, {
        invoiceId: selectedInvoiceId,
        newYTDFinal: parseFloat(editedYtdAmount),
      });

      const data = response.data;
      if (response.status === 200) {
        if (data.status === 1) {
          // alert("YTD Final updated successfully!");
          setIsYtdModalOpen(false);
          const refreshed = await fetchInvoices(
            currentPage,
            pageSize,
            selectedType,
            currentUserId,
            startDate,
            endDate,
          );
          setInvoices(refreshed);
          //  setTimeout(() => {
          //   fetchPrintedUsers();
          // }, 500);
        } else {
          showMessage("Error: " + data.message);
        }
        // Refresh data here if needed
      } else {
        showMessage("Error: " + data.message);
      }
    } catch (err) {
      console.error(err);
      console.error("Error:", err);
      showMessage("An error occured");
    } finally {
      setLoading(false);
    }
  };

  const toggleEndDateDropdown = () => {
    if (endDatePickerRef.current) {
      endDatePickerRef.current.setOpen(true);
    }
  };
  const handleClearButtonClick = () => {
    setSelectedRows([]);
    setShowCheckboxes(false);
  };

  const handleShowButtonClick = () => {
    setShowCheckboxes(true);
  };
  const handleDeleteInvoice = async (id) => {
    guardAction(PERMISSIONS.DELETE, "delete payslip", async () => {
      const confirmed = window.confirm(
        "Are you sure you want to delete this invoice?",
      );
      if (!confirmed) return;

      setLoading(true);

      try {
        const response = await axios.post(`${apiUrl}/deleteGeneratedInvoice`, {
          invoiceId: id,
        });

        if (response.data && response.data.status === 1) {
          showMessage(response.data.message);
          window.location.reload();
        } else {
          showMessage("Error: " + response.data.message);
        }
      } catch (error) {
        showMessage("An unexpected error occurred");
        console.error("Delete invoice error:", error);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleCheckboxChange = (invoiceId) => {
    // console.log(invoiceId);
    if (selectedRows.includes(invoiceId)) {
      // console.log(invoiceId);
      setSelectedRows(selectedRows.filter((id) => id !== invoiceId));
    } else {
      setSelectedRows([...selectedRows, invoiceId]);
    }
  };

  const navigate = useNavigate();
  const handleOnClick = (id) => {
    // Perform any necessary actions

    // Navigate to the /userProfile route with email as a parameter
    navigate("/invoiceDetail", { state: id });
  };

  const handleGenerateAllPdfs = () => {
    guardAction(PERMISSIONS.GENERATE_PDF, "generate PDFs", () => {
      if (selectedType === "") {
        navigate("/multipleInvoiceDetail?generate=all");
        return;
      }

      const init = async () => {
      try {
        setLoading(true);

        const totalCount = await fetchInvoiceCount(
          selectedType,
          currentUserId,
          startDate,
          endDate,
          includeArchived,
        );

        if (!totalCount || totalCount <= 0) {
          showMessage("No payslips found for selected filters");
          return;
        }

        let payFromDateInput;
        let payToDateInput;
        if (startDate && endDate) {
          payFromDateInput = convertDateFormat(startDate.toString());
          payToDateInput = convertDateFormat(endDate.toString());
        }

        const response = await axios.post(`${apiUrl}/invoiceList`, {
          page: 1,
          resultsPerPage: totalCount,
          type: selectedType,
          id: currentUserId,
          payFromDate: payFromDateInput,
          payToDate: payToDateInput,
          includeArchived: includeArchived,
        });

        const ids = (response.data?.data || []).map((inv) => inv.id);

        if (!ids.length) {
          showMessage("No payslips found for selected filters");
          return;
        }

        navigate("/multipleInvoiceDetail", { state: ids });
      } catch (error) {
        showMessage("Failed to generate PDFs for selected filters");
      } finally {
        setLoading(false);
      }
    };

    init();
    });
  };

  const handleGeneratePdfs = () => {
    guardAction(PERMISSIONS.GENERATE_PDF, "generate PDFs", () => {
      if (selectedRows.length > 0) {
        navigate("/multipleInvoiceDetail", { state: selectedRows });
      } else {
        alert("please select atleas one payslip");
      }
    });
  };
  function formatValue(value) {
    return value === "0.00" ? "-" : value;
  }

  function formatInvoiceAdditions(additions) {
    const numericValue = Number(additions);

    if (isNaN(numericValue) || numericValue % 1 !== 0) {
      // Check if it's a decimal
      const formattedValue = numericValue.toFixed(2); // Ensure 2 digits after the decimal point
      return formattedValue;
    } else {
      // If it's an integer, add .00 to make it a decimal with 2 digits after the decimal point
      return additions + ".00";
    }
  }

  // Function to handle type selection
  const handleTypeSelection = (type) => {
    if (type !== selectedType && selectedType !== "") {
      setIsBothSelected(true);
      setSelectedType("both");
    } else {
      setIsBothSelected(false);
      setSelectedType(type);
    }
  };

  const handleReset = () => {
    setSelectedType("");
    setIsBothSelected(false);
    setStartDate(null);
    setEndDate(null);
    setuserEmail("");
    setCurrentUserId(null);
    setUserName("");
    setIncludeArchived(false);
  };

  const parseYmdToDate = (ymd) => {
    if (!ymd) return null;
    const parts = ymd.toString().split("-");
    if (parts.length !== 3) return null;
    const year = Number(parts[0]);
    const monthIndex = Number(parts[1]) - 1;
    const day = Number(parts[2]);
    if (!year || monthIndex < 0 || day <= 0) return null;
    return new Date(year, monthIndex, day);
  };

  useEffect(() => {
    const fetchActiveRangeForUi = async () => {
      try {
        const response = await axios.post(`${apiUrl}/getRangeDateData`);
        const currentRange = response?.data?.currentRange;
        if (currentRange?.start_date && currentRange?.end_date) {
          const start = parseYmdToDate(currentRange.start_date);
          const end = parseYmdToDate(currentRange.end_date);
          if (start && end) {
            setStartDate(start);
            setEndDate(end);
          }
        }
      } catch (error) {
        //console.error("Failed to fetch active payroll date range:", error);
      }
    };

    fetchActiveRangeForUi();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // console.log("current");
        // console.log(currentPage);
        // console.log(pageSize);
        // console.log(selectedType);
        // console.log(currentUserId);
        // console.log(startDate);
        // console.log(endDate);
        //  console.log("end");
        const response = await fetchInvoices(
          currentPage,
          pageSize,
          selectedType,
          currentUserId,
          startDate,
          endDate,
          includeArchived,
        );
        setInvoices(response);
      } catch (error) {
        //console.error("Failed to fetch invoice list:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    currentPage,
    pageSize,
    selectedType,
    currentUserId,
    dateChangeFlag,
    includeArchived,
  ]);

  useEffect(() => {
    async function fetchData() {
      setCount(0);
      try {
        const userCount = await fetchInvoiceCount(
          selectedType,
          currentUserId,
          startDate,
          endDate,
          includeArchived,
        );
        console.log(userCount);
        setCount(userCount);
      } catch (error) {}
    }

    fetchData();
  }, [selectedType, currentUserId, dateChangeFlag, includeArchived]);

  const showMessage = (text) => {
    setMessage(text);

    // Hide the message after 2000 milliseconds (2 seconds)
    setTimeout(() => {
      setMessage(null);
    }, 2000);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSearchParams({ page });
  };

  useEffect(() => {
    if (startDate && endDate) {
      const isValidDateRange = startDate <= endDate;

      // Show message and return if the date range is invalid
      if (!isValidDateRange) {
        showMessage("Start Date should be less than end date");
        setStartDate(null);
        setEndDate(null);
        return;
      }
    }

    if (startDate != null && endDate != null) {
      handleTypeSelection("date");
      // Set the dateChangeFlag to trigger the first useEffect
      setDateChangeFlag((prevFlag) => !prevFlag);
    }
  }, [startDate, endDate]);

  const setStartDateFun = (date) => {
    setStartDate(date);
  };

  const setEndDateFun = (date) => {
    setEndDate(date);
    setShowDropdown(false);
    if (startDate != null && endDate != null) {
      handleTypeSelection("date");
    }
  };

  const handleDateChange = (date) => {
    if (startDate) {
      setStartDate(date);
      setShowDropdown(true);
    } else if (endDate) {
      setEndDate(date);
      //handleTypeSelection("date");
      setShowDropdown(false);
    }
    if (startDate && endDate) {
      handleTypeSelection("date");
    }
  };
  //get label
  const getSelectedMonthsLabel = () => {
    if (startDate && endDate) {
      const startMonth = startDate
        .toLocaleString("default", { month: "long" })
        .substring(0, 3);
      const endMonth = endDate
        .toLocaleString("default", { month: "long" })
        .substring(0, 3);
      return `${startMonth}-${endMonth}`;
    }
    return "";
  };

  const getFormattedDate = (date) => {
    if (date) {
      const options = {
        month: "short", // Short month name (e.g., "Nov")
        day: "2-digit", // Two-digit representation of the day
        year: "numeric", // Four-digit representation of the year
      };
      const month = date.toLocaleString("default", { month: "short" });
      const day = date.toLocaleString("en", { day: "2-digit" });
      const year = date.toLocaleString("en", { year: "numeric" });

      return `${month} ${day}, ${year}`;

      // return date
      //   .toLocaleDateString("default", options)
      //   .replace(/(\d+)\/(\d+)\/(\d+)/, (_, month, day, year) => {
      //     return `${month} ${day}, ${year}`;
      //   });
    }
    return "";
  };

  const handleSelectOption = (selectedOption) => {
    console.log(selectedOption.value);
    setuserEmail(selectedOption.value);
    setUserName(selectedOption.label);
    setCurrentUserId(selectedOption.value);
    setSelectOpen(false);
    handleTypeSelection("user");
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // Fetch employees from the API
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.post(`${apiUrl}/getAllUsers`);
        const raw = Array.isArray(response.data) ? response.data : [];
        setEmployees(raw.filter(isEmployeeEligibleForPayslipList));
      } catch (error) {
        //console.error("Failed to fetch employees:", error);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setSelectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectRef]);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       const response = await axios.post(`${apiUrl}/invoiceList`, {
  //         //   type: 'user', // Set your desired type, userId, payFromMonth, payToMonth, etc.
  //         //   userId: 123, // Replace with your actual user ID or variables
  //         //   payFromMonth: '2023-01-01', // Replace with your date or variables
  //         //   payToMonth: '2023-01-31', // Replace with your date or variables
  //       });
  //       setInvoices(response.data.data);
  //     } catch (error) {
  //       console.error("Failed to fetch invoice list:", error);
  //     }
  //   };

  //   fetchData();
  // }, []); // Empty dependency array to fetch data only once on component mount

  const options = [
    ...employees.map((employee) => ({
      value: employee.id || currentUserId,
      label: `${employee.name}`,
    })),
  ];

  const filteredEmployeeOptions = options.filter((option) => {
    if (!employeeSearchTerm) return true;
    return option.label
      ?.toString()
      .toLowerCase()
      .includes(employeeSearchTerm.toLowerCase());
  });

  return (
    <div className="feedback-parent-container">
      <PermissionDialog />
      <NavBar />
      <TopNavBar />
      {isYtdModalOpen && currentInvoiceData && (
        <div className="custom-modal">
          <div className="modal-content-ytd" style={{ minWidth: "400px" }}>
            <h3 style={{ marginBottom: "16px" }}>Edit YTD Amount</h3>

            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "bold",
                fontSize: "15px",
              }}
            >
              YTD Amount:
            </label>
            <input
              type="number"
              step="0.01"
              value={editedYtdAmount}
              onChange={(e) => setEditedYtdAmount(e.target.value)}
              placeholder="Enter YTD amount"
              style={{
                width: "94%",
                padding: "10px",
                marginBottom: "16px",
                fontSize: "15px",
                border: "2px solid #2E8056",
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                className="edit-ytd-submit"
                onClick={() => setIsYtdModalOpen(false)}
              >
                Cancel
              </button>
              <button className="edit-ytd-submit" onClick={handleEditYtdSubmit}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="container-invoiceList">
        <div className="invoice-list">
          <div className="admin-header invoice-list-admin-header">
            <div className="left-section">
              <h1 className="admin-header-title">
                {count > 0 ? (
                  <span className="admin-card-title-span">
                    {/* Invoice List ({count}) */}
                    Payslip
                  </span>
                ) : (
                  <span className="admin-card-title-span">Payslip</span>
                )}
                <button
                  style={{
                    marginLeft: "5px",
                    display: "inline",
                    marginTop: "0px",
                  }}
                  className={`custom-button ${deniedActionClass(canGeneratePdf)}`}
                  onClick={handleGenerateAllPdfs}
                >
                  Generate All Pdfs
                </button>
                <button
                  style={{
                    marginLeft: "5px",
                    display: "inline",
                    marginTop: "0px",
                  }}
                  onClick={handleShowButtonClick}
                  className="custom-button"
                >
                  Select-Payslips
                </button>

                <span
                  style={{ marginLeft: "1px" }}
                  className={`reset-btn-invoice-list ${
                    showCheckboxes == false ? "visibility-hidden" : ""
                  }`}
                  onClick={handleClearButtonClick}
                >
                  Clear
                </span>

                <span
                  style={{ marginLeft: "1px" }}
                  className={`reset-btn-invoice-list ${
                    showCheckboxes == false ? "visibility-hidden" : ""
                  } ${deniedActionClass(canGeneratePdf)}`}
                  onClick={handleGeneratePdfs}
                >
                  Generate Pdfs
                </span>
              </h1>
            </div>

            <div className="right-section">
              <div className="scrollable-dropdown">
                {/* <Select
                  placeholder="Choose Employee"
                  options={options}
                  value={options.find((option) => option.value === userEmail)}
                  onFocus={() => setSelectOpen(true)}
                  onBlur={() => setSelectOpen(false)}
                  onChange={(selectedOption) => {
                    setuserEmail(selectedOption.value);
                    handleTypeSelection("user");
                    //console.log("selectedOption.value ", selectedOption.value);
                    //formik.setFieldValue("employee", selectedOption.value);
                  }}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      maxHeight: "50vh",
                      overflowY: "auto",
                    }),
                    control: (provided) => ({
                      ...provided,
                      width: "15vw",
                      marginRight: "10px",
                      marginTop: "3%",
                    }),
                    option: (provided) => ({
                      ...provided,
                      fontWeight: 500, // Set the font weight for options
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      fontWeight: 500, // Set the font weight for the selected value
                    }),
                  }}
                /> */}

                <div>
                  <button
                    className={`reset-btn-invoice-list ${
                      selectedType === "" ? "visibility-hidden" : ""
                    }`}
                    onClick={handleReset}
                  >
                    Clear
                  </button>
                </div>

                <div ref={selectRef} className="custom-select">
                  <div
                    className={`custom-button custom-select-button${
                      currentUserId ? " has-value" : ""
                    }`}
                    onClick={() => {
                      setSelectOpen((prev) => {
                        const next = !prev;
                        if (next) {
                          setEmployeeSearchTerm("");
                          setTimeout(() => {
                            employeeSearchInputRef.current?.focus();
                          }, 0);
                        }
                        return next;
                      });
                    }}
                  >
                    {currentUserId ? (
                      <>
                        {userName.length > 15
                          ? `${userName.slice(0, 15)}...`
                          : userName}
                      </>
                    ) : (
                      "Choose Employee"
                    )}
                    <img
                      src={dropDownIcon}
                      alt="Dropdown Icon"
                      className="dropdown-icon"
                    />
                  </div>

                  {selectOpen && (
                    <div className="select-options">
                      <div className="select-search-container">
                        <input
                          ref={employeeSearchInputRef}
                          className="select-search-input"
                          type="text"
                          value={employeeSearchTerm}
                          placeholder="Search employee"
                          onChange={(e) =>
                            setEmployeeSearchTerm(e.target.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {filteredEmployeeOptions.map((option) => (
                        <div
                          key={option.value}
                          className="select-option"
                          onClick={() => handleSelectOption(option)}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* <DatePicker
                  style={{ marginRight: '10px' }}
                  selected={selectingStartDate ? startDate : endDate}
                  onChange={handleDateChange}
                  dateFormat="MM/dd/yyyy"
                  placeholderText={
                    selectingStartDate ? "Select Start Date" : "Select End Date"
                  }
                /> */}

                {/* <div className="custom-datepicker">
                  <div className="custom-button" onClick={toggleDropdown}>
                    <img
                      src={dropDownIcon}
                      alt="Dropdown Icon"
                      className="dropdown-icon"
                    />
                    {getSelectedMonthsLabel() || "Select Date"}
                  </div>
                  {showDropdown && (
                    <div className="date-dropdown">
                      {!startDate && (
                        <>
                          <label htmlFor="startDatePicker">Start Date:</label>
                          <DatePicker
                            id="startDatePicker"
                            selected={startDate}
                            onChange={handleDateChange}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="Select Start Date"
                            className="start-date-picker"
                          />
                        </>
                      )}
                      {startDate && (
                        <>
                          <label htmlFor="endDatePicker">End Date:</label>
                          <DatePicker
                            id="endDatePicker"
                            selected={endDate}
                            onChange={handleDateChange}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="Select End Date"
                            className="end-date-picker"
                          />
                        </>
                      )}
                    </div>
                  )}
                </div> */}

                {/* Start Date */}
                <div className="custom-datepicker">
                  <div
                    // className="custom-button"
                    className={`custom-button${startDate ? " has-date" : ""}`}
                    onClick={toggleStartDateDropdown}
                  >
                    {getFormattedDate(startDate) || "Start Date"}
                    <img
                      src={dropDownIcon}
                      alt="Dropdown Icon"
                      className="dropdown-icon"
                    />
                  </div>

                  <DatePicker
                    ref={startDatePickerRef}
                    id="startDatePicker"
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="MM/dd/yyyy"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    className="start-date-picker"
                  />
                </div>
                {/* end date  */}

                {/* End Date */}
                <div className="custom-datepicker">
                  <div
                    // className="custom-button"
                    className={`custom-button${endDate ? " has-date" : ""}`}
                    onClick={toggleEndDateDropdown}
                  >
                    {getFormattedDate(endDate) || "End Date"}
                    <img
                      src={dropDownIcon}
                      alt="Dropdown Icon"
                      className="dropdown-icon"
                    />
                  </div>
                  <div className="end-date-picker-container">
                    <DatePicker
                      ref={endDatePickerRef}
                      id="endDatePicker"
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      dateFormat="MM/dd/yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      className="end-date-picker"
                      popperProps={{
                        placement: "bottom-end", // Adjust placement as needed
                        modifiers: {
                          preventOverflow: {
                            enabled: true,
                          },
                          offset: {
                            enabled: true,
                            offset: "160, 0", // Adjust offset as needed (horizontal, vertical)
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="archived-checkbox-container">
                  <input
                    id="invoice-list-archived"
                    className="archived-checkbox"
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(e) => setIncludeArchived(e.target.checked)}
                  />
                  <label
                    className="archived-checkbox-label"
                    htmlFor="invoice-list-archived"
                  >
                    Archived
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="invoice-table-wrapper">
            {loading && (
              <div className="spinner-container">
                <TailSpin
                  type="TailSpin"
                  color="rgb(155, 236, 34)"
                  height={70}
                  width={70}
                  timeout={5000}
                />
              </div>
            )}
            {message && (
              // <div className="custom-popup">
              //   {message}
              // </div>
              <div className="custom-modal">
                <div className="modal-content">{message}</div>
              </div>
            )}
            <table className="invoice-table even-invoice-table">
              <thead>
                <tr className="printedListHeader">
                  {showCheckboxes && <th></th>}
                  <th>Invoice Id</th>
                  <th>Employee Id</th>
                  <th>Name</th>
                  <th>Pay Period</th>
                  <th>Pay Rate($)</th>
                  <th>Salary($)</th>
                  <th>Hours</th>
                  <th>Additions($)</th>
                  <th>Deductions($)</th>
                  <th>Amount($)</th>
                  <th>YTD Amount($)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, index) => (
                  <tr
                    key={invoice.id}
                    onClick={() => {
                      if (!showCheckboxes) {
                        handleOnClick(invoice.id);
                      }
                    }}
                  >
                    {showCheckboxes && (
                      <td>
                        <input
                          type="checkbox"
                          className="insideSelections"
                          checked={selectedRows.includes(invoice.id)}
                          onChange={() => handleCheckboxChange(invoice.id)}
                        />
                      </td>
                    )}
                    <td>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        #{invoice.id}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        #{invoice.userId}
                      </Link>
                    </td>
                    <td>
                      {/* <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        {invoice.name}
                      </Link> */}
                      <div className="user-container">
                        {invoice.image ? (
                          <div className="invoice-list-image">
                            <img src={invoice.image} alt="Employee Image" />
                          </div>
                        ) : (
                          <div className="invoice-list-image">
                            <img
                              src={DefaultEmployeeProfile}
                              alt="Default Employee Image"
                            />
                          </div>
                        )}
                        <Link
                          to={"/invoiceDetail"}
                          state={invoice.id}
                          className="user-link"
                        >
                          {invoice.name}
                        </Link>
                      </div>
                    </td>
                    <td>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        {invoice.payPeriod}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        {/* ${invoice.payRate} */}
                        {formatInvoiceAdditions(invoice.payRate)}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        {invoice.salary === undefined ||
                        invoice.salary === "" ||
                        invoice.salary === null
                          ? "-"
                          : formatInvoiceAdditions(invoice.salary)}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        {/* {invoice.hours}:{invoice.minutes} */}
                        {formatValue(
                          invoice.hours +
                            ":" +
                            invoice.minutes.toString().padStart(2, "0"),
                        )}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        {formatValue(formatInvoiceAdditions(invoice.Additions))}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        {/* ${invoice.Deductions} */}
                        {formatValue(
                          formatInvoiceAdditions(invoice.Deductions),
                        )}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        {/* ${invoice.Amount} */}
                        {formatInvoiceAdditions(invoice.Amount)}
                      </Link>
                    </td>
                    <td>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          guardAction(PERMISSIONS.MANAGE_YTD, "edit YTD amount", () => {
                            setIsYtdModalOpen(true);
                            setSelectedInvoiceId(invoice.id);
                            setCurrentInvoiceData(invoice);
                            setEditedYtdAmount(
                              invoice.YTDFinal || invoice.YTDAmount || 0,
                            );
                          });
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          marginLeft: "8px",
                          cursor: "pointer",
                        }}
                        className={deniedActionClass(canManageYtd)}
                        title="Edit YTD Amount"
                      >
                        ✏️
                      </button>
                      <Link
                        to={"/invoiceDetail"}
                        state={invoice.id}
                        className="user-link"
                      >
                        {formatInvoiceAdditions(
                          invoice.YTDFinal || invoice.YTDAmount,
                        )}
                      </Link>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {" "}
                      {/* Prevent click from triggering on row */}
                      <Link
                        to={"/editInvoice?id=" + invoice.id}
                        state={invoice.id}
                        className={`user-link icon-margin ${deniedActionClass(canEditPayslip)}`}
                        onClick={(e) => {
                          e.preventDefault();
                          guardAction(PERMISSIONS.EDIT, "edit payslip", () => {
                            navigate("/editInvoice?id=" + invoice.id, {
                              state: invoice.id,
                            });
                          });
                        }}
                      >
                        <img
                          src={editIcon}
                          style={{ width: "15px" }}
                          alt="Edit Icon"
                          className="edit-icon admin-panel-icon"
                        />
                      </Link>
                      <img
                        src={deleteIcon}
                        alt="Delete Icon"
                        style={{ width: "15px" }}
                        className={`delete-icon admin-panel-icon ${deniedActionClass(canDeletePayslip)}`}
                        onClick={() => handleDeleteInvoice(invoice.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {count !== undefined && count > 0 ? (
          <Pagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalUsers={count}
            onPageChange={handlePageChange}
          />
        ) : null}
      </div>
    </div>
  );
}

export default InvoiceList;
